import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { CreateEditService } from '@core/http/create-edit-service';

export type TutorSelectItem = {
  idUsuario: string;
  nombreCompleto: string;
  email?: string | null;
  numeroIdentificacion?: string | null;
  imagen?: string | null;
};

export type TutorSelectDialogData = {
  email: string;
  options: TutorSelectItem[];
};

export type TutorSelectDialogResult = {
  selected: TutorSelectItem;
};

@Component({
  selector: 'app-tutor-select-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule
  ],
  templateUrl: './tutor-select-dialog.component.html',
  styleUrl: './tutor-select-dialog.component.scss'
})
export class TutorSelectDialogComponent {
  ctrl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });

  constructor(
    private ref: MatDialogRef<TutorSelectDialogComponent, TutorSelectDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: TutorSelectDialogData,
    private ceService: CreateEditService
  ) {
    // ✅ preseleccionar el primero si existe
    const first = data?.options?.[0]?.idUsuario ?? '';
    if (first) this.ctrl.setValue(first);
  }

  cancelar(): void {
    this.ref.close(null);
  }

  async aceptar(): Promise<void> {
    if (this.ctrl.invalid) {
      this.ctrl.markAsTouched();
      return;
    }

    const id = this.ctrl.value;
    const selected = (this.data?.options ?? []).find(x => x.idUsuario === id) ?? null;

    if (!selected) {
      this.ctrl.setErrors({ required: true });
      return;
    }

    let numeroIdentificacion = selected.numeroIdentificacion ?? null;
    let imagen = selected.imagen ?? null;

    if (!numeroIdentificacion || !imagen) {
      const extra = await this.obtenerDatosTutorPorIdUsuario(selected.idUsuario);

      if (!numeroIdentificacion) {
        numeroIdentificacion = extra.numeroIdentificacion;
      }

      if (!imagen) {
        imagen = extra.imagen;
      }
    }

    this.ref.close({
      selected: {
        ...selected,
        numeroIdentificacion: numeroIdentificacion ?? null,
        imagen: imagen ?? null
      }
    });
  }

  trackById(_: number, item: TutorSelectItem): string {
    return item.idUsuario;
  }

  private async obtenerDatosTutorPorIdUsuario(
    idUsuario: string
  ): Promise<{ numeroIdentificacion: string | null; imagen: string | null }> {
    const id = String(idUsuario ?? '').trim();
    if (!id) {
      return {
        numeroIdentificacion: null,
        imagen: null
      };
    }

    try {
      const res: any = await firstValueFrom(this.ceService.getpatientByIdUser(id));
      const data = Array.isArray(res?.data) ? res.data[0] : (res?.data ?? null);

      const numero =
        data?.numeroIdentificacion ??
        data?.NumeroIdentificacion ??
        null;

      const imagenRaw =
        data?.imagen ??
        data?.Imagen ??
        null;

      const imagen = imagenRaw
        ? (String(imagenRaw).startsWith('data:image/')
          ? String(imagenRaw)
          : `data:image/png;base64,${String(imagenRaw)}`)
        : null;

      return {
        numeroIdentificacion: numero ? String(numero).trim() : null,
        imagen
      };
    } catch {
      return {
        numeroIdentificacion: null,
        imagen: null
      };
    }
  }
}
